import { ComponentState } from "../../shared/types/ComponentState";
import { AmountType, TransactionType } from "../../shared/types/Transaction";
import { CheckAbbreviatedRecipientTypes, frequencyDict, transactionTypes } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { ErrorDetail } from "../../create-request/models/shared/ServiceErrorDetails";
import { DistributionReason } from '../../create-request/models/internal-transfer/DistributionReason';
import { TaxRemittance } from "../../create-request/models/wire/WireInfo";

export class StandingAuthorizationForm extends ComponentState {
    transactionType?: TransactionType | '';
    valid?: boolean;
    standingAuthorizationDetails: StandingAuthDetails;
    serviceError: StandingAuthFormServiceError;
    prefilledFlow: {
        state: 'started' | 'inProgress' | 'complete' | '',
        name: 'edit-review' | 'edit-table' | 'edit-view' | ''
    };
    extraPrompts?: ExtraPrompts;
    isSubmitAndCreateBundledSa?: boolean;
    createStandingAuthInProgress?: boolean;
    acknowledgeCallV3Failed?: boolean;
}

export type StandingAuthDetails = AchStandingAuthDetails | CheckStandingAuthDetails | ItStandingAuthDetails | WireStandingAuthDetails;
export type ExtraPrompts = AchExtraPrompts | CheckExtraPrompts


export interface AchStandingAuthForm extends StandingAuthorizationForm {
    transactionType: 'ach';
    extraPrompts: AchExtraPrompts;
    standingAuthorizationDetails: AchStandingAuthDetails;
}

export interface CheckStandingAuthForm extends StandingAuthorizationForm {
    transactionType: 'check',
    transactionDetails: CheckStandingAuthDetails;
}

export interface ItStandingAuthForm extends StandingAuthorizationForm {
    transactionType: 'internal-transfer';
    standingAuthorizationDetails: ItStandingAuthDetails;
}

export interface WireStandingAuthForm extends StandingAuthorizationForm {
    transactionType: 'wire',
    standingAuthorizationDetails: WireStandingAuthDetails;
}

export interface AchExtraPrompts {
    transferDirection: 'incoming' | 'outgoing' | '';
    standingAuthId: string; // first letter of transaction type + cashieringRequestId + cashieringRequestSecondId
    frequency?: keyof typeof frequencyDict;
}

export interface CheckExtraPrompts{

}

export interface CheckStandingAuthDetails {
    payeeType?: 'firstParty' | 'thirdParty';
    payeeName?: string;
    defaultPayee?: string[];
    additionalPayeeName1?: string;
    additionalPayeeName2?: string;
    recipient?: CheckAbbreviatedRecipientTypes;
    checkReason?: string;
    checkMemo?: string;
    authorizationNickname?: string;
    addresseeName?: string;
    addresseeNameCont?: string;
    addresseeNameCont2?: string;
    streetAddress?: string;
    streetAddressCont?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    fedTaxPercent?: string;
    stateTaxPercent?: string;
    distributionReason?: string;
    taxWithholdingsFormGroup?: TaxWithholdingFields;
    modifiedPayeeCheckbox?: boolean;
    modifiedPayeeName1?: string;
    modifiedPayeeName2?: string;
    modifiedPayeeName3?: string;
    nickname?: string;
    useConstructedPayee?: boolean;
    payee?: any;
}

export interface ItStandingAuthDetails {
    toAccount: string;
    authorizationNickname: string;
    fedTaxPercent: string;
    stateTaxPercent: string;
    taxWithholdingsFormGroup?: TaxWithholdingFields;
}

export interface WireStandingAuthDetails extends receivingAccountHolder, receivingbankDetails, receivingAccountHolderIso {
    recipient: 'firstParty' | 'thirdParty' | '';
    isEscrow: boolean;
    toAccount: string;
    aba: string;
    transmissionNotes: string;
    intermediarySelection: 'yes' | 'no' | '';
    intermediaryName: string;
    intermediaryNameCont: string;
    intermediaryAccountNumber: string;
    intermediaryAddress: string;
    intermediaryAddressCont: string;
    intermediaryCountry: string;
    intermediaryPhone: string;
    authorizationNickname: string;
    fedTaxPercent: string;
    stateTaxPercent: string;
    taxWithholdingsFormGroup?: TaxWithholdingFields;
    payeeName?: string;
    modifiedPayeeCheckbox?: boolean;
    modifiedPayeeName1?: string;
    modifiedPayeeName2?: string;
    isoModifiedPayeeName?: string;
    // v3 properties
    name: string;
    receivingAccountHolderStreetName: string;
    receivingAccountHolderStreetNumber: string;
    receivingAccountHolderApartmentSuiteEtc: string;
    receivingAccountHolderCity: string,
    receivingAccountHolderState: string,
    receivingAccountHolderZip: string,
    receivingAccountHolderDepartment: string;
    receivingAccountHolderBuildingName: string;
    receivingAccountHolderFloor: string;
    receivingAccountHolderTownLocationName: string;
    receivingAccountHolderDistrict: string;
    intermediaryStreetNumber: string;
    intermediaryStreetName: string;
    intermediaryApartmentSuiteEtc: string;
    intermediaryCity: string;
    intermediaryState: string;
    intermediaryZip: string;
    intermediaryDepartment: string;
    intermediaryBuildingName: string;
    intermediaryFloor: string;
    receivingBankCity: string;
    receivingBankState: string;
    isISOCompliant?: boolean;
    taxPayerId: string;
    taxTypeCode: string;
    taxMonth: string;
    taxYear: string;
}

export interface AchStandingAuthDetails {
    recurringFrequency: string;
    endDate: string;
    secondProcessDate: string;
    withdrawalDate: string;
    completionDate: string;
    settlementDate: string;
    fundsAvailableToTrade: string;
    amount: string;
    amountType: AmountType;
    transferAmount: string;
    contributionReason: string;
    selectedAuthorization: string;
    transferDirection: string;
    trustIncomePercent?: string;
    trustPrincipalPercent?: string;
    trustTaxCategory?: string;
    trustTaxCode?: string;
    trustPaidFor?: string;
    trustComment?: string;
    costBasisMethod?: string
    taxWithholdingsFormGroup?: TaxWithholdingFields;
}

export interface TaxWithholdingFields {
    fedTaxPercent: string;
    fedTaxAmount: string;
    stateTaxPercent: string;
    stateTaxAmount: string;
    distributionReason: string;
    stateHasTaxes?: boolean;
    distributionReasonMap?: DistributionReason[];
}

interface receivingAccountHolder {
    receivingAccountHolderName1: string;
    receivingAccountHolderName2: string;
    receivingAccountHolderAddress1: string;
    receivingAccountHolderAddress2: string;
    receivingAccountHolderCountry: string;
}

interface receivingAccountHolderIso {
    name: string;
    receivingAccountHolderStreetNumber: string;
    receivingAccountHolderStreetName: string;
    receivingAccountHolderApartmentSuiteEtc: string;
    // country codes have 2 characters
    countryCode: string;
    countryDescription?: string;
  }

interface receivingbankDetails {
    receivingBankName1: string;
    receivingBankName2: string;
    receivingBankAddress1: string;
    receivingBankAddress2: string;
    receivingBankCountry: string;
    bankPhoneNumber: string;
    bankID: string;
}

export interface StandingAuthFormServiceError {
    status: number;
    url: string;
    name: string;
    message: string;
    correlationId: string;
    hasError: boolean;
    errorList?: ErrorDetail[];
}

export const isAchExtraPrompts = (prompts: ExtraPrompts | undefined): prompts is AchExtraPrompts => {
    if (prompts && 'transferDirection' in prompts) {
        return true;
    }
    return false
}

export const isAchStandingAuthDetails = (details: StandingAuthDetails | undefined): details is AchStandingAuthDetails => {
    if (details && 'completionDate' in details) {
        return true;
    }
    return false;
}

export const isAchFormSA = (form: StandingAuthorizationForm): form is AchStandingAuthForm => {
    return form.transactionType === transactionTypes.ach;
}

export const isCheckStandingAuthFormSA = (form: StandingAuthorizationForm): form is CheckStandingAuthForm => {
    return form.transactionType === transactionTypes.check;
}

export const isInternalTransferFormSA = (form: StandingAuthorizationForm): form is ItStandingAuthForm => {
    return form.transactionType === transactionTypes.internalTransfer;
}

export const isWireFormSA = (form: StandingAuthorizationForm): form is WireStandingAuthForm => {
    return form.transactionType === transactionTypes.wire;
}

export const isItStandingAuthDetails = (details: StandingAuthDetails): details is ItStandingAuthDetails => {
    if (details && 'toAccount' in details) {
        return true;
    }
    return false;
}

export const isCheckStandingAuthDetails = (details: StandingAuthDetails): details is CheckStandingAuthDetails => {
    if (details && 'payeeType' in details) {
        return true;
    }
    return false;
}
